import { DateTime } from 'luxon';
import { notEmpty } from '../../utilities/notEmpty';
import { Logger } from '../../logging/LoggerService';
import { IMobilityGroupLegacyResponse } from '../IMobilityGroupResponse';
import { eTransactionCategory } from '../../enums/eTransactionType';
import { eEntityType } from '../../enums/eEntityType';
import { ePartyType } from '../../enums/ePartyType';
import { ePartyPurpose } from '../../enums/ePartyPurpose';
import { IThirdParties } from '../interfaces/IThirdParties';
import { IServiceOrderPartyData } from '../interfaces/IServiceOrderPartyData';
import { IServiceOrderPartyEntityData } from '../interfaces/IServiceOrderPartyEntityData';
import { IServiceOrderPartyPersonData } from '../interfaces/IServiceOrderPartyPersonData';
import { IServiceOrderResponse } from '../interfaces/IServiceOrderResponse';
import { IRefiDiscount } from '../interfaces/IRefiDiscount';
import { ILenderInformation } from '../interfaces/ILenderInformation';
import { IClientData } from '../interfaces/IClient';
import { IAddressResponse } from '../interfaces/IAddressResponse';
import { IPartySignatory } from '../interfaces/IPartySignatory';
import { IPropertyAddress } from '../interfaces/IPropertyAddress';
import { createPartyEntity } from '../interfaces/IPartyEntity';
import { IParty } from '../interfaces/IParty';
import { createPartyPerson } from '../interfaces/IPartyPerson';
import { IOrderDetailsData } from '../interfaces/IOrderDetailsData';
import { convertUndefinedToNull } from '../../utilities/convertUndefinedToNull';

export const sanitizePhone = (phone?: string) => (phone ? phone.replace(/\D/g, '') : phone);

const convertApiPartyToDataParty = (serviceData: IServiceOrderPartyData): IParty => {
  return serviceData.partyType?.toLowerCase() === ePartyType.entity.toLowerCase()
    ? createPartyEntity({
        id: serviceData.id,
        purpose: serviceData.purpose,
        entity_type: (serviceData as IServiceOrderPartyEntityData).entityType,
        entity_name: (serviceData as IServiceOrderPartyEntityData).name,
        signatories: (serviceData as IServiceOrderPartyEntityData).signatories.map(
          (signatory) =>
            ({
              ...signatory,
              first_name: signatory.firstName,
              last_name: signatory.lastName,
              middle_name: signatory.middleName,
              phone_number: signatory.phoneNumber,
              party_sequence: signatory.partySequence,
              address: {
                ...signatory.address,
                street_address: (signatory.address as any)?.streetAddress ?? signatory.address?.street_address,
                state: (signatory.address as any)?.stateAbbreviation ?? signatory.address?.state,
                zip_code: (signatory.address as any)?.zipCode ?? signatory.address?.zip_code,
              },
            }) as IPartySignatory
        ),
        party_sequence: serviceData.partySequence,
        address: {
          ...serviceData.address,
          street_address: serviceData.address?.street_address ?? (serviceData.address as any)?.streetAddress,
          state: serviceData.address?.state ?? (serviceData.address as any)?.stateAbbreviation,
          zip_code: serviceData.address?.zip_code ?? (serviceData.address as any)?.zipCode,
        } as IPropertyAddress,
      })
    : createPartyPerson({
        ...serviceData,
        id: serviceData.id,
        purpose: serviceData.purpose,
        first_name: (serviceData as IServiceOrderPartyPersonData).firstName,
        last_name: (serviceData as IServiceOrderPartyPersonData).lastName,
        middle_name: (serviceData as IServiceOrderPartyPersonData).middleName,
        ssn: (serviceData as IServiceOrderPartyPersonData).ssn,
        marital_status: (serviceData as IServiceOrderPartyPersonData).maritalStatus,
        email: (serviceData as IServiceOrderPartyPersonData).email,
        phone_number: sanitizePhone((serviceData as IServiceOrderPartyPersonData).phoneNumber),
        party_sequence: serviceData.partySequence,
        lender_user_id: (serviceData as IServiceOrderPartyPersonData).lenderUserId,
        address: {
          ...serviceData.address,
          street_address: serviceData.address?.street_address ?? (serviceData.address as any)?.streetAddress,
          state: serviceData.address?.state ?? (serviceData.address as any)?.stateAbbreviation,
          zip_code: serviceData.address?.zip_code ?? (serviceData.address as any)?.zipCode,
        } as IPropertyAddress,
        date_of_birth: (serviceData as IServiceOrderPartyPersonData).dateOfBirth,
        zuid: (serviceData as IServiceOrderPartyPersonData).zuid,
      });
};

const convertApiPartiesToDataParties = (
  parties: IServiceOrderPartyData[],
  thirdParties: IThirdParties,
  lender: ILenderInformation,
  client: IClientData,
  created: Date,
  state: string
): IParty[] => {
  // TODO: move isOrderInStateWithMultipleUnderwriters to BE. It belongs to Order Create logic
  const isOrderInStateWithMultipleUnderwriters = ['AR', 'ID', 'NM'].includes(state);
  const { notarizingAgent, settlementAgent, titleIssuingAgent, underwriter } = thirdParties;
  return [
    ...parties.filter((t) => t.purpose === ePartyPurpose.borrower).map(convertApiPartyToDataParty),
    ...parties.filter((t) => t.purpose === ePartyPurpose.buyer).map(convertApiPartyToDataParty),
    ...parties.filter((t) => t.purpose === ePartyPurpose.seller).map(convertApiPartyToDataParty),
    lender.id
      ? createPartyEntity({
          purpose: ePartyPurpose.lender,
          entity_name: lender.name!,
          entity_type: eEntityType.other,
          id: lender.id,
        })
      : null,
    client.id
      ? createPartyEntity({
          purpose: ePartyPurpose.client,
          entity_name: client.name!,
          entity_type: eEntityType.other,
          id: client.id,
        })
      : null,
    ...parties.filter((t) => t.purpose === ePartyPurpose.order_contact).map(convertApiPartyToDataParty),
    createPartyEntity({
      purpose: ePartyPurpose.underwriter,
      entity_type: eEntityType.other,
      entity_name: underwriter
        ? underwriter.label
        : isOrderInStateWithMultipleUnderwriters
          ? 'Fulfilled through third party'
          : 'None Associated',
      entity_value: underwriter ? underwriter.value : undefined,
      signatories: [],
      read_only: true,
    }),
    createPartyEntity({
      type: ePartyType.entity,
      purpose: ePartyPurpose.settlementAgent,
      entity_type: eEntityType.other,
      entity_name: settlementAgent
        ? settlementAgent.label
        : isOrderInStateWithMultipleUnderwriters
          ? 'Fulfilled through third party'
          : 'None Associated',
      entity_value: settlementAgent ? settlementAgent.value : undefined,
      signatories: [],
      read_only: true,
    }),
    // FYI: always hide Title Agent line (even "None Associated") for orders created before deploy date
    created > DateTime.fromISO('2019-09-12T01:00:00Z').toJSDate()
      ? createPartyEntity({
          type: ePartyType.entity,
          purpose: ePartyPurpose.titleIssuingAgent,
          entity_type: eEntityType.other,
          entity_name: titleIssuingAgent
            ? titleIssuingAgent.label
            : isOrderInStateWithMultipleUnderwriters
              ? 'Fulfilled through third party'
              : 'None Associated',
          entity_value: titleIssuingAgent ? titleIssuingAgent.value : undefined,
          signatories: [],
          read_only: true,
        })
      : null,
    // FYI: always hide Notarizing Agent line (even "None Associated") for orders created before deploy date
    created > DateTime.fromISO('2019-09-12T01:00:00Z').toJSDate()
      ? createPartyEntity({
          type: ePartyType.entity,
          purpose: ePartyPurpose.notarizingAgent,
          entity_type: eEntityType.other,
          entity_name: notarizingAgent ? notarizingAgent.label : 'None Associated',
          entity_value: notarizingAgent ? notarizingAgent.value : undefined,
          signatories: [],
          read_only: true,
        })
      : null,
  ].filter(notEmpty);
};

export const convertAddressFromAPIToData = (addressData: IAddressResponse): IPropertyAddress => ({
  city: addressData.city,
  state: addressData.stateAbbreviation,
  street_address: convertUndefinedToNull(addressData.streetAddress),
  unit: convertUndefinedToNull(addressData.unit),
  zip_code: addressData.zipCode,
  countyName: addressData.countyName,
  countyFips: addressData.countyFips,
  countyState: convertUndefinedToNull(addressData.stateAbbreviation),
});

export const convertOrderDetailsFromAPIToData = (serviceData: IServiceOrderResponse): IOrderDetailsData => {
  try {
    const lender = {
      id: convertUndefinedToNull(serviceData.lenderCompany && serviceData.lenderCompany.id),
      name: convertUndefinedToNull(serviceData.lenderCompany && serviceData.lenderCompany.name),
      bulkRateQualified: convertUndefinedToNull(
        serviceData.lenderCompany && serviceData.lenderCompany.bulkRateQualified
      ),
      mortgageeClause: convertUndefinedToNull(serviceData.lenderCompany && serviceData.lenderCompany.mortgageeClause),
    };
    const client: IClientData = {
      id: convertUndefinedToNull(serviceData.clientCompany && serviceData.clientCompany.id)!,
      name: convertUndefinedToNull(serviceData.clientCompany && serviceData.clientCompany.name)!,
      sprucePowered: (serviceData.clientCompany && serviceData.clientCompany.sprucePowered) || false,
    };
    const parties = convertApiPartiesToDataParties(
      serviceData.parties || [],
      serviceData.thirdParties,
      lender,
      client,
      DateTime.fromISO(serviceData.createdAt).toJSDate(),
      serviceData.propertyAddress.stateAbbreviation
    );
    return {
      transaction: {
        id: serviceData.id,
        closing_date: convertUndefinedToNull(serviceData.closesOn),
        file_number: serviceData.fileNumber,
        purchase_price:
          serviceData.transactionCategory === eTransactionCategory.Purchase
            ? convertUndefinedToNull(serviceData.purchasePrice)
            : null,
        estimated_closing_date: convertUndefinedToNull(serviceData.estimatedClosingDate),
        estimated_property_value:
          serviceData.transactionCategory === eTransactionCategory.Refinance
            ? convertUndefinedToNull(serviceData.purchasePrice)
            : null,
        external_party_transaction_id: convertUndefinedToNull(serviceData.externalPartyTransactionId),
        loan_amount: convertUndefinedToNull(serviceData.loanAmount),
        loan_number: convertUndefinedToNull(serviceData.loanNumber),
        loan_type: convertUndefinedToNull(serviceData.loanType),
        order_group_category: convertUndefinedToNull(serviceData.orderGroupCategory),
        order_group_id: convertUndefinedToNull(serviceData.orderGroupId),
        occupancy_type: convertUndefinedToNull(serviceData.occupancyType),
        owner_name: convertUndefinedToNull(serviceData.ownerName),
        property_type: convertUndefinedToNull(serviceData.propertyType),
        rate_lock_expiration: convertUndefinedToNull(serviceData.rateLockExpiration),
        relocation_employer_name: convertUndefinedToNull(serviceData.relocationEmployerName),
        solar_panels: convertUndefinedToNull(serviceData.solarPanels),
        transaction_category: serviceData.transactionCategory || eTransactionCategory.Refinance,
        mobility_transaction_type: serviceData.transactionType,
        mobility_group: {
          ...serviceData.mobilityGroup,
          external_id: (serviceData.mobilityGroup as any)?.externalId,
          client_id: (serviceData.mobilityGroup as any)?.clientId,
          program_type: (serviceData.mobilityGroup as any)?.programType,
          take_title: (serviceData.mobilityGroup as any)?.takeTitle,
        } as IMobilityGroupLegacyResponse,
        escrow_file_id: serviceData.escrowFile?.id,
        splitClosing: serviceData.splitClosing,
        services: serviceData.services,
        firstMortgagePayoffAccount: serviceData.firstMortgagePayoffAccount,
        firstMortgagePayoffName: serviceData.firstMortgagePayoffName,
        titleCommitmentId: serviceData.titleCommitmentId,
        taxCertificationId: serviceData.taxCertificationId,
        hasRedvisionTask: serviceData.hasRedvisionTask,
      },
      lender,
      client,
      property: convertAddressFromAPIToData(serviceData.propertyAddress),
      orderInformation: {
        created: serviceData.createdAt,
        createdBy: convertUndefinedToNull(serviceData.createdBy),
        updated: convertUndefinedToNull(serviceData.updatedAt),
        status: convertUndefinedToNull(serviceData.orderPhase),
        externalStatus: convertUndefinedToNull(serviceData.externalStatus),
        refiDiscount: convertUndefinedToNull(
          serviceData.refiDiscount
            ? ({
                id: serviceData.refiDiscount?.id,
                order_id: (serviceData.refiDiscount as any)?.orderId ?? serviceData.refiDiscount?.order_id,
                eligibility: serviceData.refiDiscount?.eligibility,
                applied: serviceData.refiDiscount?.applied,
                finalized_at: (serviceData.refiDiscount as any)?.finalizedAt ?? serviceData.refiDiscount?.finalized_at,
                created_at: (serviceData.refiDiscount as any)?.createdAt ?? serviceData.refiDiscount?.created_at,
              } as IRefiDiscount)
            : undefined
        ),
        fundingDate: convertUndefinedToNull(serviceData.fundsOn),
        orderLinks: serviceData.orderLinks || [],
      },
      parties,
      fees_version: serviceData.feesVersion,
      latestCancellationRequest: serviceData.latestCancellationRequest,
      isBiccaEnabled: serviceData.isBiccaEnabled,
      isZimConversationCreated: serviceData.isZimConversationCreated,
    };
  } catch (e: any) {
    Logger.error('Data convert error', e);
    throw new Error('Data convert error');
  }
};
